import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import SimpleForm from "../../components/SimpleForm";
import { Snackbar, Alert } from "@mui/material";

/* ---------- Axios ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: { "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

/* ---------- Helpers ---------- */
const buildInitials = (fields) =>
  fields.reduce((acc, f) => {
    if (f.default !== undefined) {
      acc[f.name] = f.default;
    } else if (f.type === "checkboxGroup") {
      acc[f.name] = [];
    } else if (f.type === "checkbox") {
      acc[f.name] = false;
    } else if (f.type === "number") {
      acc[f.name] = 0;
    } else {
      acc[f.name] = "";
    }
    return acc;
  }, {});

/* ---------- Validation ---------- */
const PERMISSION_SCHEMA = yup.object().shape({
  module: yup.string().required("required"),
  actions: yup
    .array()
    .of(yup.string().oneOf(["create", "read", "update", "delete"]))
    .min(1, "select at least one")
    .required("required"),
  roleId: yup
    .number()
    .transform((val, orig) =>
      typeof orig === "string" && orig.trim() !== "" ? Number(orig) : val
    )
    .typeError("select a role")
    .required("required"),
});

/* ---------- API helpers ---------- */
async function getRoles() {
  const res = await api.get("/api/role", {
    // prevent stale cached responses
    params: { _: Date.now() },
    
  });
  return res.data.data;
}

async function postPermission(body) {
  try {
    const res = await api.post("/api/permission", body);
    return { data: res.data, error: null };
  } catch (err) {
    const responseData = err?.response?.data;
    const message =
      responseData?.error?.errors?.[0]?.message ||
      responseData?.message ||
      err.message ||
      "Request failed";
    return { data: responseData, error: message };
  }
}

/* ---------- Page ---------- */
export default function CreatePermission() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchRoles = async () => {
      try {
        setLoadingRoles(true);
        const list = await getRoles();
        if (mounted) setRoles(Array.isArray(list) ? list : []);
      } catch {
        if (mounted)
          setToast({ open: true, msg: "Failed to load roles", sev: "error" });
      } finally {
        if (mounted) setLoadingRoles(false);
      }
    };

    // initial load
    fetchRoles();

    // refetch when window regains focus (keeps dropdown fresh)
    const onFocus = () => fetchRoles();
    window.addEventListener("focus", onFocus);

    return () => {
      mounted = false;
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const FIELDS = useMemo(() => {
    const roleOptions = roles.map((r) => ({ label: r.name, value: r.id }));

    const moduleOptions = [
      "Ware House",
      "Customer",
      "Category",
      "Item",
      "Balance",
      "Balance Sheet",
      "Expence",
      "Car Information",
      "Car Operation",
      "Store",
      "Purchase",
      "Sales",
      "Lending",
      "Return",
      "Stock Out",
       "Expense Report",
  "Sales Report",
  "Purchase Report",
  "Lending Report",
  "Return Report",
  "Stockout Report",
  "Income Report",
  "Profit Report",
      "FS",
      "Z Balance",
    ].map((m) => ({ label: m, value: m }));

    return [
      {
        name: "module",
        label: "Module",
        type: "select",
        span: 2,
        required: true,
        options: moduleOptions,
      },
      {
        name: "actions",
        label: "Actions",
        type: "checkboxGroup",
        span: 2,
        required: true,
        options: [
          { label: "Create", value: "create" },
          { label: "Read", value: "read" },
          { label: "Update", value: "update" },
          { label: "Delete", value: "delete" },
        ],
        default: [],
      },
      {
        name: "roleId",
        label: loadingRoles ? "Loading roles..." : "Role",
        type: "select",
        span: 2,
        required: true,
        options: roleOptions,
        props: {
          disabled: loadingRoles,
        },
      },
    ];
  }, [roles, loadingRoles]);

  const INITIALS = useMemo(() => buildInitials(FIELDS), [FIELDS]);

  const handleSubmit = async (
  values,
  { setSubmitting, setErrors, resetForm }
) => {

  // convert checkbox array -> object
  const actionsObject = {
    create: values.actions.includes("create"),
    read: values.actions.includes("read"),
    update: values.actions.includes("update"),
    delete: values.actions.includes("delete"),
  };

  const payload = {
    roleId: Number(values.roleId),
    module: values.module,
    actions: actionsObject,
  };

  const { data, error } = await postPermission(payload);

  setSubmitting(false);

  const isSequelize =
    data?.error?.name?.includes("Sequelize") ||
    Array.isArray(data?.error?.errors);

  if (isSequelize) {
    const first = data?.error?.errors?.[0];
    const path = first?.path || "module";
    const msg = first?.message || data?.message || "Validation error";

    setErrors({ [path]: msg });

    setToast({
      open: true,
      msg: `Error: ${msg}`,
      sev: "error",
    });

    return;
  }

  if (error) {
    setToast({
      open: true,
      msg: `Error: ${error}`,
      sev: "error",
    });
    return;
  }

  const msg = data?.message || "Permission created successfully";

  setToast({
    open: true,
    msg,
    sev: "success",
  });

  resetForm();
};

  return (
    <>
      <SimpleForm
        title="CREATE PERMISSION"
        subtitle="Define module actions for a role"
        Header={Header}
        fields={FIELDS}
        initialValues={INITIALS}
        validation={PERMISSION_SCHEMA}
        onSubmit={handleSubmit}
        submitLabel="Create Permission"
        columns={4}
      />

      <Snackbar
       anchorOrigin={{ vertical: 'top', horizontal: 'right'}}
      sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          variant="filled"
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
